import { css } from "@compiled/react";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";
import { richTextToHTML } from "src/clients/uniform-client/enhancers/utils";

import { media } from "@atlaskit/primitives/responsive";

import {
    templateBenefitsContentfulRelayFragment$data,
    templateBenefitsContentfulRelayFragment$key
} from "@/__generated_contentful__/templateBenefitsContentfulRelayFragment.graphql";
import { SanitizedRichText } from "@/components/confluence/util/sanitized-rich-text";

type TemplateBenefitsProps = {
    templateBenefits: templateBenefitsContentfulRelayFragment$key;
};

type BenefitItem = NonNullable<
    NonNullable<
        templateBenefitsContentfulRelayFragment$data["templateBenefitContainerCollection"]
    >["items"]
>[number];

const tabbedSectionStyle = css({
    "marginBlockStart": 56,
    "marginInlineStart": "auto",
    "marginInlineEnd": "auto",

    "@media (max-width: 39.99rem)": {
        maxWidth: "100%"
    }
});

const stepContainerStyle = css({
    "display": "flex",
    "justifyContent": "space-between",
    "paddingBlockEnd": "3rem",
    "marginBlockEnd": "3rem",
    "borderBlockEnd": "2px solid #ebecf0",
    "gap": "2rem",

    "@media (max-width: 39.99rem)": {
        flexDirection: "column"
    },

    ":last-of-type": {
        borderBlockEnd: "none"
    }
});

const tabbedSectionHeading = css({
    "fontSize": "2.25rem",
    "lineHeight": "2.75rem",
    "fontWeight": 500,
    "letterSpacing": 0.3,
    "color": "#1d3059",
    "fontFamily": "var(--bxp-charlie-display-font-family)",
    "margin": 0,
    "marginBlockEnd": 40,

    "@media (max-width: 47.99rem)": {
        fontSize: "1.5rem",
        lineHeight: "1.75rem"
    }
});

const stepHeading = css({
    fontSize: "1.5rem",
    lineHeight: "2rem",
    fontWeight: 500,
    letterSpacing: 0.3,
    // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#1d3059",
    // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
    // eslint-disable-next-line local/ensure-wac-token-usage
    fontFamily: "var(--bxp-charlie-display-font-family)",
    margin: 0,

    [media.below.sm]: {
        fontSize: "1.25rem",
        lineHeight: "1.5rem"
    }
});

const stepContentWrapper = css({
    "display": "flex",
    "flexDirection": "column",
    "gap": "2rem",

    "@media (max-width: 39.99rem)": {
        gap: "1.5rem"
    }
});

const TemplateBenefitsStep = ({
    templateBenefitNameExternal,
    shortDescription
}: NonNullable<BenefitItem>) => {
    const name = templateBenefitNameExternal;
    const description = shortDescription?.json
        ? richTextToHTML(shortDescription.json)
        : "";
    return (
        <div css={stepContainerStyle}>
            <div css={stepContentWrapper}>
                <h3 css={stepHeading}>{name}</h3>
                {description && <SanitizedRichText innerHTML={description} />}
            </div>
        </div>
    );
};

const TemplateBenefitsRelayFragment = graphql`
    fragment templateBenefitsContentfulRelayFragment on TemplateBenefitContainer {
        benefitsTitle
        templateBenefitContainerCollection {
            items {
                templateBenefitNameExternal
                shortDescription {
                    json
                }
            }
        }
    }
`;

export const TemplateBenefits = ({
    templateBenefits
}: TemplateBenefitsProps) => {
    const data = useFragment(TemplateBenefitsRelayFragment, templateBenefits);

    const benefits = data.templateBenefitContainerCollection?.items ?? [];

    return (
        <section css={tabbedSectionStyle}>
            <h2 css={tabbedSectionHeading}>{data.benefitsTitle}</h2>
            {benefits.map(
                (benefitDetail, index) =>
                    benefitDetail && (
                        <TemplateBenefitsStep
                            key={`benefit-${index}`}
                            {...benefitDetail}
                        />
                    )
            )}
        </section>
    );
};
